// src/components/layouts/renderers/CortesFrame.tsx
// Branded cortes-channel frame modeled on a CapCut combination preset
// (Presset.1). Coordinates are for the fixed 1080×1920 design canvas —
// PostFrameComposition wraps non-aspectAware renderers in a scaled canvas.
import React from 'react';
import { AbsoluteFill } from 'remotion';
import type {
  CortesConfig,
  CortesRect,
  CortesTextAnchor,
  CortesTextStyle,
  LayoutRendererProps,
} from '@/lib/layouts/types';
import {
  CORTES_BASE_TEXT_PX,
  DEFAULT_CORTES_HANDLE_STYLE,
  DEFAULT_CORTES_HEADLINE_STYLE,
} from '@/lib/layouts/cortesDefaults';
import { hexToRgba } from '@/lib/layers/hexToRgba';
import { useLayerFont } from '@/hooks/use-layer-font';
import { clampFillerSpeed } from '@/lib/layouts/clampFillerSpeed';
import { isImageFillerUrl, resolveFillerPlaylist } from '@/lib/layouts/fillerPlaylist';
import { resolveCortesGeometry, resolveCortesPreset } from '@/lib/layouts/resolveCortesPreset';
import { FillerPlaylist } from './SatisfatorioFrame';

const W = 1080;
const H = 1920;
const FONT_FALLBACK = 'system-ui,-apple-system,sans-serif';
const FULL_BLEED: CortesRect = { x: 0, y: 0, w: 1, h: 1 };
type PhotoTestId = 'cortes-photo1' | 'cortes-photo2' | 'cortes-photo3' | 'cortes-photo4';

const rectStyle = (r: CortesRect): React.CSSProperties => ({
  position: 'absolute',
  left: r.x * W,
  top: r.y * H,
  width: r.w * W,
  height: r.h * H,
  // Preflight in the app clamps img to viewport width; render bundle has no stylesheet
  maxWidth: 'none',
  maxHeight: 'none',
});

const shadowCss = (s: NonNullable<CortesTextStyle['shadow']>): string => {
  const rad = (s.angle * Math.PI) / 180;
  const dx = Math.cos(rad) * s.distance;
  const dy = -Math.sin(rad) * s.distance; // +angle points up, CSS +y is down
  return `${dx.toFixed(1)}px ${dy.toFixed(1)}px ${s.distance}px ${hexToRgba(s.color, s.alpha)}`;
};

const CortesText: React.FC<{
  text: string;
  anchor: CortesTextAnchor;
  style: CortesTextStyle;
  boxColorOverride?: string;
  boxWidthPercent?: number;
  testId: string;
}> = ({ text, anchor, style, boxColorOverride, boxWidthPercent, testId }) => {
  useLayerFont(style.fontName, style.fontUrl);
  const fontPx = style.fontSize ?? CORTES_BASE_TEXT_PX * anchor.scale;
  const mode = style.boxMode ?? 'hug';
  const boxColor = mode === 'none' ? undefined : (boxColorOverride ?? style.boxColor);
  const textAlign = style.align ?? 'center';
  const bar = mode === 'bar';
  const hugWidthPx = !bar && typeof boxWidthPercent === 'number'
    ? Math.round((Math.min(100, Math.max(50, boxWidthPercent)) / 100) * W)
    : undefined;

  const inner: React.CSSProperties = {
    fontFamily: style.fontName ?? FONT_FALLBACK,
    fontWeight: 800,
    fontSize: fontPx,
    lineHeight: 1 + (style.lineSpacing ?? 0.1),
    color: style.color,
    WebkitTextStroke:
      style.strokeColor && style.strokeWidth
        ? `${(style.strokeWidth * fontPx).toFixed(1)}px ${style.strokeColor}`
        : undefined,
    paintOrder: 'stroke fill',
    textShadow: style.shadow ? shadowCss(style.shadow) : undefined,
    backgroundColor: boxColor ? hexToRgba(boxColor, style.boxAlpha ?? 1) : undefined,
    // bar spans full width; hug/none pad symmetrically around the text
    display: bar ? 'block' : 'inline-block',
    width: bar ? '100%' : (hugWidthPx != null ? `${hugWidthPx}px` : undefined),
    minWidth: hugWidthPx != null ? `${hugWidthPx}px` : undefined,
    padding: boxColor ? `${fontPx * 0.15}px ${fontPx * 0.45}px` : undefined,
    boxSizing: 'border-box',
    boxShadow: bar
      ? '0 1px 0 rgba(0,0,0,0.85), 0 8px 18px rgba(0,0,0,0.35)'
      : undefined,
  };

  return (
    <div
      data-autocut-over=""
      style={{
        position: 'absolute',
        // bar mode spans the whole width; hug/none are anchored + centered on x
        left: bar ? 0 : anchor.x * W,
        top: anchor.y * H,
        width: bar ? '100%' : undefined,
        maxWidth: bar ? undefined : W * 0.94,
        transform: bar ? 'translateY(-50%)' : 'translate(-50%, -50%)',
        textAlign,
        whiteSpace: 'pre-wrap',
      }}
    >
      <div data-testid={testId} style={inner}>
        {text}
      </div>
    </div>
  );
};

export const CortesFrame: React.FC<LayoutRendererProps<CortesConfig>> = ({ config, media, renderClip }) => {
  const g = resolveCortesGeometry(config);
  const textBox = resolveCortesPreset(config) === 'caixa-vermelha';
  const textBar = resolveCortesPreset(config) === 'barra-branca';
  const headlineStyle: CortesTextStyle = {
    ...DEFAULT_CORTES_HEADLINE_STYLE,
    ...(config.headlineStyle ?? {}),
    color: config.headlineColor ?? config.headlineStyle?.color ?? DEFAULT_CORTES_HEADLINE_STYLE.color,
    fontName: config.headlineFontFamily ?? config.headlineStyle?.fontName,
    fontUrl: config.headlineFontUrl ?? config.headlineStyle?.fontUrl,
    fontSize: config.headlineFontSize ?? config.headlineStyle?.fontSize,
  };
  const handleStyle = { ...DEFAULT_CORTES_HANDLE_STYLE, ...(config.handleStyle ?? {}) };

  const photoCell = (
    url: string | undefined,
    rect: CortesRect | undefined,
    imgTestId: PhotoTestId,
    fillerUrls: string[] | undefined,
    fillerFits: CortesConfig['photo1FillerFits'],
    fillerPositions: CortesConfig['photo1FillerPositions'],
  ) => {
    if (!rect) return null;
    const urls = resolveFillerPlaylist({ fillerUrl: url, fillerUrls });
    if (urls.some((item) => !isImageFillerUrl(item))) {
      return (
        <div
          data-testid={`${imgTestId}-filler`}
          style={{ ...rectStyle(rect), overflow: 'hidden', backgroundColor: '#000' }}
        >
          <FillerPlaylist
            urls={urls}
            fillerSpeed={clampFillerSpeed(config.fillerSpeed)}
            regionW={rect.w * W}
            regionH={rect.h * H}
            fits={fillerFits}
            positions={fillerPositions}
            slot={imgTestId.replace('cortes-', '')}
          />
        </div>
      );
    }
    if (url) {
      return (
        <img
          data-testid={imgTestId}
          data-autocut-over=""
          src={url}
          alt=""
          style={{ ...rectStyle(rect), objectFit: 'cover' }}
        />
      );
    }
    return null;
  };

  return (
    <AbsoluteFill style={{ backgroundColor: '#000', overflow: 'hidden' }}>
      {textBox && !config.backgroundUrl && renderClip && (
        <div
          data-testid="cortes-blur-bg"
          style={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
        >
          <div
            style={{
              position: 'absolute',
              inset: '-14%',
              filter: 'blur(28px)',
              transform: 'scale(1.08)',
            }}
          >
            {renderClip({ muted: true, slot: 'background' })}
          </div>
        </div>
      )}
      {config.backgroundUrl && !textBar && (
        <img
          data-testid="cortes-bg"
          src={config.backgroundUrl}
          alt=""
          style={{ ...rectStyle(g.background ?? FULL_BLEED), objectFit: 'cover' }}
        />
      )}
      <div
        data-testid="cortes-clip"
        style={{ ...rectStyle(g.clip), overflow: 'hidden', backgroundColor: '#000' }}
      >
        {media}
      </div>
      {photoCell(
        config.photo1Url,
        g.photo1,
        'cortes-photo1',
        config.photo1FillerUrls,
        config.photo1FillerFits,
        config.photo1FillerPositions,
      )}
      {photoCell(
        config.photo2Url,
        g.photo2,
        'cortes-photo2',
        config.photo2FillerUrls,
        config.photo2FillerFits,
        config.photo2FillerPositions,
      )}
      {photoCell(
        config.photo3Url,
        g.photo3,
        'cortes-photo3',
        config.photo3FillerUrls,
        config.photo3FillerFits,
        config.photo3FillerPositions,
      )}
      {photoCell(
        config.photo4Url,
        g.photo4,
        'cortes-photo4',
        config.photo4FillerUrls,
        config.photo4FillerFits,
        config.photo4FillerPositions,
      )}
      {config.headline && (
        <CortesText
          text={config.headline}
          anchor={g.headline}
          style={headlineStyle}
          boxColorOverride={config.headlineBoxColor}
          boxWidthPercent={config.headlineBoxWidth}
          testId="cortes-headline"
        />
      )}
      {!textBox && config.handle && (
        <CortesText text={config.handle} anchor={g.handle} style={handleStyle} testId="cortes-handle" />
      )}
    </AbsoluteFill>
  );
};
